/**
 * @file index.tsx
 *
 * @fileoverview Renders a string type field on
 * an entity as a text input.
 */
import React, { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  getPlaceholder,
  getSetting,
} from '~/jobiqo/components/Form/helpers/formSettings';
import { EntityFieldRules } from '~/jobiqo/models/config';
import { EntityField } from '~/jobiqo/models/entity';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { useFieldRules } from '../../hooks/useFieldRules';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { CloseIcon } from '~/vcl/icons/Close';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import FlexItems from '~/vcl/atoms/06-layout/03-flex';
import { AddIcon } from '~/vcl/icons/Add';
import { FormDescription } from '~/vcl/atoms/01-forms/11-form-item/02-form-description';
import { FormattedMessage } from 'react-intl';
import * as Yup from 'yup';
import { getCommonTranslations } from '~/jobiqo/utils/intl';

// Props used by this component.
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

/**
 * Validates a field based on the entity field type.
 *
 * @param value
 *  The value to validate.
 * @param entityField
 *  The entity field to validate.
 * @returns
 *  True if the value is valid, false otherwise.
 */
function validateField(value: string, entityField: EntityField) {
  if (entityField.type === 'email') {
    const valid = Yup.string().email().isValidSync(value);
    return valid;
  }
  return true;
}

export default function StringWidget({
  entityField,
  fieldRules = {},
  required: requiredProp = false,
  defaultValue,
  ...props
}: Props) {
  const { setValue, getValues, control, setError } = useFormContext();
  const values = getValues();
  const { hidden, disabled, required } = useFieldRules(
    entityField.id,
    values,
    fieldRules,
  );
  const { EMAIL_VALIDATION } = getCommonTranslations();

  // When field is hidden reset field state. this needs to be done inside a hook
  // otherwise it will not work.
  useEffect(() => {
    if (hidden && !!values[entityField.id]) {
      setValue(entityField.id, null);
    }
  }, [hidden, values, entityField]);

  if (hidden) {
    return null;
  }

  if (entityField.multiple) {
    const { fields, remove, append } = useFieldArray({
      control,
      name: entityField.id,
    });

    useEffect(() => {
      if (defaultValue) {
        setValue(entityField.id, [defaultValue, ...[]]);
      }
    }, [defaultValue]);

    return (
      <div>
        <>
          {fields.map((fieldItem, index) => {
            const fieldName = `${entityField.id}.${index}`;
            return (
              <FlexItems
                key={fieldItem.id}
                flexWrap="nowrap"
                alignItems="center"
              >
                <Controller
                  name={fieldName}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        name={fieldName}
                        id={fieldName}
                        type={entityField.type}
                        required={
                          entityField.required || requiredProp || required
                        }
                        disabled={entityField.disabled || disabled}
                        error={!!error}
                        {...(index === 0 && { label: entityField.label })}
                        size={40}
                        placeholder={getPlaceholder(entityField)}
                        maxLength={getSetting('max_length', entityField)}
                        // Set the value instead of using the field array
                        // update because it regenerates the id of the item
                        // which destroys the focus of the input.
                        onBlur={(e) => {
                          if (entityField.type === 'integer') {
                            setValue(
                              fieldName,
                              Number.parseInt(e.currentTarget.value),
                            );
                          } else {
                            setValue(fieldName, e.currentTarget.value);
                          }
                        }}
                      />
                      {error && <InlineError message={error.message} />}
                    </>
                  )}
                />
                <Button
                  raised={false}
                  iconed={true}
                  buttonStyle="danger"
                  type="button"
                  style={{
                    marginTop: '1rem',
                  }}
                  onClick={() => {
                    remove(index);
                  }}
                >
                  <CloseIcon title="remove" />
                  <span>
                    <FormattedMessage
                      id="remove_email"
                      defaultMessage="Remove email"
                    />
                  </span>
                </Button>
                <Spacing />
              </FlexItems>
            );
          })}
          <Spacing />
          <Controller
            name={`${entityField.id}_add`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div>
                <FlexItems gap={3} alignItems="center">
                  <Input
                    {...field}
                    name={`${entityField.id}_add`}
                    id={`${entityField.id}_add`}
                    data-test-id="add-email-input"
                    type={entityField.type}
                    required={
                      values[entityField.id]?.length === 0 &&
                      (entityField.required || requiredProp || required)
                    }
                    {...(fields.length === 0 && { label: entityField.label })}
                    disabled={entityField.disabled || disabled}
                    error={!!error}
                    size={40}
                    placeholder={getPlaceholder(entityField)}
                    maxLength={getSetting('max_length', entityField)}
                    onBlur={(e) => {
                      // validate email.
                      if (
                        field.value &&
                        validateField(field.value, entityField)
                      ) {
                        append(field.value);
                        field.onChange('');
                        // if the target is of type submit we want to
                        // make sure it submits
                        if (
                          (e.relatedTarget as HTMLButtonElement)?.type ===
                          'submit'
                        ) {
                          (e.relatedTarget as HTMLButtonElement).click();
                        }
                      }
                    }}
                  />
                  <Button
                    raised={false}
                    iconed={true}
                    buttonStyle="primary"
                    // We no longer disable the button, avoids keyboard trapping.
                    type="button"
                    style={{
                      marginTop: '1rem',
                    }}
                    data-test-id="button-add-email"
                    onClick={() => {
                      const validEmail = validateField(
                        field.value,
                        entityField,
                      );
                      if (!validEmail) {
                        setError(`${entityField.id}_add`, {
                          message: EMAIL_VALIDATION,
                        });
                      }
                      if (field.value && validEmail) {
                        append(field.value);
                        field.onChange('');
                      }
                    }}
                  >
                    <AddIcon title="add" />
                    <span>
                      <FormattedMessage
                        id="add_email"
                        defaultMessage="Add email"
                      />
                    </span>
                  </Button>
                </FlexItems>
                {error && (
                  <Spacing>
                    <InlineError message={error.message} />
                  </Spacing>
                )}
              </div>
            )}
          />
          <FormDescription>{entityField.description}</FormDescription>
        </>
      </div>
    );
  } else {
    return (
      <Controller
        name={entityField.id}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <>
            <Input
              {...field}
              id={field.name}
              type={entityField.type}
              required={entityField.required || required}
              disabled={entityField.disabled || disabled}
              error={!!error}
              label={entityField.label}
              size={40}
              description={entityField.description}
              placeholder={getPlaceholder(entityField)}
              maxLength={getSetting('max_length', entityField)}
              min={getSetting('min', entityField)}
              max={getSetting('max', entityField)}
              {...props}
              onChange={(e) => {
                if (entityField.type === 'integer') {
                  setValue(field.name, Number.parseInt(e.currentTarget.value), {
                    shouldValidate: true,
                  });
                } else {
                  setValue(field.name, e.currentTarget.value, {
                    shouldValidate: true,
                  });
                }
              }}
            />
            {!!error && <InlineError message={error.message} />}
          </>
        )}
      />
    );
  }
}

export { StringWidget };
